import { type ReactNode, useMemo, useState } from "react";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { View } from "@common/types/views/views";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { filterApplyReportTemplateRowsByTemplate } from "./applyReportTemplateFilter";
import { filterApplyReportTemplateRows } from "./applyReportTemplateSearch";
import type { ApplyReportTemplateRow } from "./applyReportTemplateRows";
import type { ApplyReportTemplateTableCallbacks } from "./cells/ApplyReportTemplateOverridesCell";
import {
    applyReportTemplateViewConfig,
    defaultApplyReportTemplateView,
} from "./applyReportTemplateView";
import styles from "./ApplyReportTemplate.module.scss";

interface Props {
    rows: ApplyReportTemplateRow[] | null;
    templateKey?: string;
    isError: boolean;
    selection: RowSelectionState;
    onSelectionChange: OnChangeFn<RowSelectionState>;
    onRetry: () => void;
    onOpenSettings: (row: ApplyReportTemplateRow) => void;
    footer: ReactNode;
}

const baseTableMeta: ViewTableMeta<
    ApplyReportTemplateTableCallbacks,
    undefined
> = {
    parentSelection: "self",
    getRowSelectionDisabledTooltip: (row) =>
        typeof row === "object" &&
        row !== null &&
        "disabledTooltip" in row &&
        typeof row.disabledTooltip === "string"
            ? row.disabledTooltip
            : undefined,
};

export const ApplyReportTemplateTable = ({
    rows,
    templateKey,
    isError,
    selection,
    onSelectionChange,
    onRetry,
    onOpenSettings,
    footer,
}: Props) => {
    const tableMeta = useMemo(
        () => ({ ...baseTableMeta, callbacks: { onOpenSettings } }),
        [onOpenSettings],
    );
    const [view, setView] = useState<View>(defaultApplyReportTemplateView);
    const [search, setSearch] = useState("");
    const visibleRows = useMemo(() => {
        if (!rows) {
            return null;
        }
        return filterApplyReportTemplateRowsByTemplate({
            rows: filterApplyReportTemplateRows({ rows, search }),
            templateKey,
        });
    }, [rows, search, templateKey]);

    return (
        <>
            <TablePageHeader
                isFullPage
                search={search}
                onSearchChange={setSearch}
            />
            <ViewTable
                data={isError ? [] : visibleRows}
                view={view}
                config={applyReportTemplateViewConfig}
                onViewChange={setView}
                rowSelection={selection}
                onRowSelectionChange={onSelectionChange}
                enableRowSelection={(row) => row.isSelectable}
                getRowClassName={(row) =>
                    row.disabledTooltip ? styles.blockedRow : undefined
                }
                tableMeta={tableMeta}
                enableColumnResizing
                autoExpandOnMount
                loadingView={() => <Loader />}
                emptyView={() =>
                    isError ? (
                        <EmptyState
                            header="Failed to load clients"
                            ctaText="Retry"
                            action={onRetry}
                        />
                    ) : (
                        <EmptyState
                            header="No clients found"
                            body={
                                templateKey === undefined
                                    ? "No clients or entities match your search."
                                    : "No clients or entities using this template match your search."
                            }
                        />
                    )
                }
                footer={footer}
            />
        </>
    );
};
